/**
 * `round CN-A`, `CN-A.1`: **the stage actions a screen must recognise, as the
 * WIRE carries them.**
 *
 * `02` §2.4.1's transition table names each action in French, and the door
 * (`POST /api/v1/dossiers/{id}/advance`) takes and answers those same words.
 * A screen that renders the door's list needs to recognise a handful of them ,
 * the two verdict outcomes, the one that sends a file to analysis , and
 * `hakili/no-french-outside-catalogue` is right that a French literal in a
 * screen is a copy nobody corrects. **These are not copy. They are wire
 * values**, the same class as a status or a right key, and this is where wire
 * values live.
 *
 * `services/api/.../stage-machine.spec.ts` asserts the machine's table carries
 * every word here, so a renamed transition reddens the api rather than
 * silently un-matching a control.
 */
export const STAGE_ACTIONS = {
  reportConforming: "Rapport conforme, demander les pièces",
  reportNonConforming: "Clore sans suite : véhicule non conforme",
  sendToAnalysis: "Dossier complet, lancer l'analyse Hakili",
  scheduleExpertise: "Programmer l'expertise",
  fileReport: "Enregistrer le rapport",
  // The three the list carries that are NOT a transition: two gestures in
  // place (`to: null`), which the pieces card owns, and the closure, which has
  // its own control on every open stage. A stage panel skips them.
  uploadOnBehalf: "Téléverser à la place du client",
  remindOnDocument: "Relancer sur cette pièce",
  closeWithoutFollowUp: "Clore le dossier sans suite",
  // Round `L-2`, `M3` (WF-CON-14 steps 2 and 3, `M0 (d)`; B10): the three the
  // « Soumis » and « Offre » panels read by name , the manual recording opens
  // a form, the buyer's retention is his own act (greyed), the placement is
  // the insurance round's (greyed).
  recordOffer: "Enregistrer une offre reçue",
  clientRetainedOffer: "Le client a retenu une offre",
  launchInsurancePlacement: "Lancer le placement d'assurance",
  // Round `L-3b`, `M2` (WF-CON-14 steps 2 and 4; M0 (d), B13): the manual refusal
  // opens a form with the motive (`POST /submissions/{id}/refusal`); the
  // declaration goes by its own door (`POST /dossiers/{id}/all-refused`),
  // disabled with 05 §2.6.9's sentence until every submission is `refus`.
  recordRefusal: "Enregistrer un refus",
  allBanksRefused: "Toutes les banques ont refusé",
  // Round `L-4`, `M3 (b)` (WF-CON-11, WF-CON-12): the filter's run and the failing branch's exit;
  // the exit is greyed with the guard's own sentence until the file carries a failing verdict.
  runFilter: "Lancer le filtre Hakili",
  // Round 10a (WF-CON-13, decision D-1010-3): the send waits on the chargée's employment verification; the « pret » panel
  // draws this action greyed, with the gate's sentence, until the act exists on the dossier.
  confirmSubmission: "Confirmer la soumission",
  // Round `AA-3a1` (D-1018-1): the chargée's own decision at `analyse`, never automatic , the filter computes and the
  // file stays; this row moves it to `pret` once at least one bank was eligible at the last computation. The
  // console's « Refuser » is `rejectAtFilter` below (a reason typed by her, the wire string unchanged).
  validateToPret: "Valider et passer à Prêt",
  // Round `PRET-SEND-FILTER-GUARD` (`P0-DOS-074`): the machine's own move when a field of the reading changes on a file at `pret`; no control draws it
  // (`automatic`), so a screen never offers it and the chargée never presses it.
  returnToAnalysis: "Revenir à l'analyse",
  rejectAtFilter: "Écarter au filtre Hakili",
  // Round `T-1`, `M2` (the insurance parked by record, 02 §2.4.1 and WF-CON-15 amended): the
  // « Offre » panel's own control , the file passes to the conditions with the insurance at
  // the client's charge; the placement's word stays in the machine and is drawn by nobody.
  proceedToConditions: "Passer aux conditions préalables",
  // Round `T-1b`, `M2` (WF-CON-18 step 4): the « Conditions » panel draws the advance itself, guarded
  // in the drawing's French; the lift is its own door, never an action of the list.
  allConditionsLifted: "Toutes les conditions sont levées",
  liftCondition: "Lever une condition",
  issuePickupVoucher: "Émettre le bon de retrait",
} as const;

/** The words a stage panel never draws as an advance. */
export const IN_PLACE_ACTIONS: readonly string[] = [
  STAGE_ACTIONS.fileReport,
  STAGE_ACTIONS.scheduleExpertise,
  STAGE_ACTIONS.uploadOnBehalf,
  STAGE_ACTIONS.remindOnDocument,
  STAGE_ACTIONS.closeWithoutFollowUp,
];

export type StageAction = (typeof STAGE_ACTIONS)[keyof typeof STAGE_ACTIONS];
